import {
    BabyLoungeIcon,
    CustomerServiceCenterIcon,
    ElevatorIcon,
    EscalatorIcon,
    ExchangeIcon,
    GiftRedemptionIcon,
    InformationIcon,
    RestRoomIcon,
    StorageIcon,
    StrollerRentalIcon,
} from '@/components/icons/floor-guide/categories';
import { TaxRefundIcon } from '@/components/icons/floor-guide/categories/TaxRefund';
import { CATEGORY_CODE_ID, CATEGORY_ORDER, MAPPING_APP_LANGUAGE_TO_MAP_LANGUAGE, MAPPING_MENU_LANGUAGE, OBJECT_STYLE_SET } from '@/constants/floor-guide';
import { RegexConstants } from '@/constants/regex';
import { CategoryType } from '@/enums/floor-guide';
import type { BuildingCategory, PoiContent } from '@/services/dto';
import type { languageType } from '@/stores';
import type { PoiItem } from '@/types/map';
import { LANG_TYPE, type DabeeoMap, type DabeeoMapData } from 'dabeeomaps';
import type { ICurrentPois } from 'dabeeomaps/dist/src/model/map/ICurrentPois';
import type { IPoi } from 'dabeeomaps/dist/src/model/map/IPoi';
import type { TFunction } from 'i18next';

export const MAP_CATEGORY_CODE_TO_ICON = {
    [CATEGORY_CODE_ID[CategoryType.BABY_LOUNGE]]: <BabyLoungeIcon />,
    [CATEGORY_CODE_ID[CategoryType.CUSTOMER_SERVICE_CENTER]]: <CustomerServiceCenterIcon />,
    [CATEGORY_CODE_ID[CategoryType.ELEVATOR]]: <ElevatorIcon />,
    [CATEGORY_CODE_ID[CategoryType.ESCALATOR]]: <EscalatorIcon />,
    [CATEGORY_CODE_ID[CategoryType.EXCHANGE]]: <ExchangeIcon />,
    [CATEGORY_CODE_ID[CategoryType.GIFT_REDEMPTION]]: <GiftRedemptionIcon />,
    [CATEGORY_CODE_ID[CategoryType.INFORMATION]]: <InformationIcon />,
    [CATEGORY_CODE_ID[CategoryType.LOCKER]]: <StorageIcon />,
    [CATEGORY_CODE_ID[CategoryType.RESTROOM]]: <RestRoomIcon />,
    [CATEGORY_CODE_ID[CategoryType.STROLLER_RENTAL]]: <StrollerRentalIcon />,
    [CATEGORY_CODE_ID[CategoryType.TAX_REFUND]]: <TaxRefundIcon />,
};

const DEFAULT_BACKGROUND_COLOR = '#FFFFFF';
const CONTRAST_BACKGROUND_COLOR = '#000000';

export const getCategoryIcon = (categoryCode?: string) => {
    const defaultIcon = <InformationIcon />;
    if (!categoryCode) {
        return defaultIcon;
    }

    return MAP_CATEGORY_CODE_TO_ICON[categoryCode as keyof typeof MAP_CATEGORY_CODE_TO_ICON] || defaultIcon;
};

export const sortCategories = (categories?: BuildingCategory[]) => {
    if (!Array.isArray(categories) || !categories?.length) {
        return [];
    }

    return [...categories].sort((a, b) => {
        const orderA = CATEGORY_ORDER[a.clientCode] ?? Infinity;
        const orderB = CATEGORY_ORDER[b.clientCode] ?? Infinity;
        return orderA - orderB;
    });
};

export const updateObjectGroup = (mapData: DabeeoMapData | undefined, objectGroup: { [key: string]: any[] }) => {
    if (!mapData) {
        return;
    }
    const objects = mapData.dataObject.getObjects();
    if (!objects) {
        return;
    }
    const groupKeys = Object.keys(objectGroup);
    groupKeys.forEach((key) => {
        objectGroup[key] = [];
    });
    // 분류
    for (const obj of objects) {
        const key = obj.objectType;
        if (key && groupKeys.includes(key)) {
            objectGroup[key].push(obj);
        }
    }
};

export const updateObjectStyle = (currentMap: DabeeoMap | undefined, objectGroup: { [key: string]: any[] }) => {
    const groupKeys = Object.keys(objectGroup);
    groupKeys.forEach((key) => {
        const typedKey = key as keyof typeof OBJECT_STYLE_SET;
        const colorSet = OBJECT_STYLE_SET[typedKey];
        const ids = objectGroup[key].map((obj) => obj.id);
        currentMap?.objects?.set({
            ids,
            color: colorSet.color,
            strokeColor: colorSet.stroke,
            strokeWidth: 10,
            strokeOpacity: 1,
            opacity: 1,
        });
    });
    currentMap?.context?.setCanvasColor(CONTRAST_BACKGROUND_COLOR);
};

export const resetObjectStyle = (currentMap: DabeeoMap | undefined) => {
    currentMap?.objects.reset();
    currentMap?.context.setCanvasColor(DEFAULT_BACKGROUND_COLOR);
};

export const getFloorLocalizedName = (floor: { name: Record<string, string> }, language: string) => {
    return floor.name[language] || floor.name.ko || '';
};

export const getFloorMapName = (floor: { name: Record<string, string> }) => {
    return floor.name.ko || '';
};

export const generateFloorName = (rawFloorName: string) => {
    if (!rawFloorName) return '';
    const lowerCaseFloorName = rawFloorName.toLowerCase();
    const hasFCharacter = RegexConstants.FLOOR_NUMBER_WITH_F_REGEX.test(lowerCaseFloorName);
    if (hasFCharacter) {
        const floorNumber = lowerCaseFloorName.replace(RegexConstants.FLOOR_NUMBER_WITH_F_REGEX, (_, digits) => digits).trim();
        return floorNumber;
    }
    return rawFloorName;
};

export const generateFloorNameWithTTS = (t: TFunction, floorName: string, selected: boolean = false) => {
    const status = selected ? t('common.button.tts.status_selected') : t('common.button.tts.status_not_selected');

    return `${status} ${floorName} ${t('common.button.tts.tab_button')}`;
};

export const generateAllFloorsGuideWithTTS = (t: TFunction, buttonText: string, selected: boolean = false) => {
    const status = selected ? t('common.button.tts.status_selected') : t('common.button.tts.status_not_selected');

    return `${status} ${buttonText} ${t('common.button.tts.switch_button')}`;
};

export const generateMapLanguage = (appLanguage: languageType) => {
    return MAPPING_APP_LANGUAGE_TO_MAP_LANGUAGE[appLanguage] ?? LANG_TYPE.DEFAULT;
};

export const syncMapLanguage = (map: DabeeoMap | undefined, appLanguage: languageType) => {
    if (!map) return;
    map.context?.changeLanguage?.(generateMapLanguage(appLanguage) as LANG_TYPE);
};

export const generateMenuKey = (appLanguage: languageType) => {
    const key = MAPPING_MENU_LANGUAGE[appLanguage] ?? 'shopPrdcNm';
    return key;
};

export const generateGnbSvgColor = (isGnbActive: boolean, isContrast: boolean) => {
    if (isContrast && isGnbActive) {
        return '--color-not-contrast-black-222';
    }

    if (isGnbActive && !isContrast) {
        return '--color-not-contrast-white';
    }

    return '--color-not-contrast-white';
};

export const generateNearbyOptions = (poiItem?: PoiItem) => {
    return {
        isVisible: false,
        sortOption: {
            floorId: poiItem?.floorId ?? '',
            center: {
                x: poiItem?.position?.x ?? 0,
                y: poiItem?.position?.y ?? 0,
            },
            byAsc: true,
        },
    };
};

export const getNearbyPoiList = (poiItem?: PoiItem, currentMap?: DabeeoMap) => {
    if (!poiItem || !currentMap) {
        return [];
    }

    const options: ICurrentPois = {
        isVisible: false,
        sortOption: {
            floorId: poiItem?.floorId ?? '',
            center: {
                x: poiItem?.position?.x ?? 0,
                y: poiItem?.position?.y ?? 0,
            },
            byAsc: true,
        },
    };

    const orderedPoiList = currentMap?.pois?.getCurrentPois(options);

    if (!Array.isArray(orderedPoiList) || orderedPoiList?.length === 0) {
        return [];
    }

    return orderedPoiList as PoiItem[];
};

export const isNearbyPoiAvailable = (poiContentList?: PoiContent[], studioPoiId?: string) => {
    if (!Array.isArray(poiContentList) || !poiContentList?.length || !studioPoiId) {
        return false;
    }

    return poiContentList.some((poi) => poi.studioPoiId === studioPoiId);
};

export const getNearestPoiItem = (poiList?: PoiItem[], itemIndex: number = 1) => {
    if (!Array.isArray(poiList) || poiList?.length === 0) {
        return undefined;
    }

    if (itemIndex >= poiList.length) {
        return poiList[0];
    }

    return poiList[itemIndex];
};

export const getNearestAvailablePoiItem = (poiList?: PoiItem[], poiContentList?: PoiContent[]) => {
    if (!Array.isArray(poiList) || poiList.length === 0 || !Array.isArray(poiContentList) || poiContentList.length === 0) {
        return undefined;
    }

    if (poiList.length === 1) {
        const first = poiList[0];
        return isNearbyPoiAvailable(poiContentList, first.id) ? first : undefined;
    }

    let result: PoiItem | undefined = undefined;

    for (let i = 1; i < poiList.length; i++) {
        const poi = poiList[i];
        if (isNearbyPoiAvailable(poiContentList, poi.id)) {
            result = poi;
            break;
        }
    }

    if (!result) {
        const first = poiList[0];
        if (isNearbyPoiAvailable(poiContentList, first.id)) {
            result = first;
        }
    }

    return result;
};

export const sortPoiByTitle = (poiList?: IPoi[], locale: string = 'en') => {
    if (!Array.isArray(poiList) || poiList.length === 0) {
        return [];
    }

    return [...poiList].sort((a, b) =>
        (a?.title ?? '').localeCompare(b?.title ?? '', locale, {
            sensitivity: 'base',
        })
    );
};

export const getPoiItemByTitle = (filteredPoi?: IPoi | IPoi[], title?: string) => {
    if (filteredPoi === null || filteredPoi === undefined) {
        return undefined;
    }

    if (!Array.isArray(filteredPoi)) {
        return filteredPoi;
    }
    const sortedPoiList = sortPoiByTitle(filteredPoi);

    if (sortedPoiList?.length === 0) {
        return undefined;
    }

    const findPoi = sortedPoiList.find((poi) => poi?.title === title);

    if (findPoi) {
        return findPoi;
    }

    return sortedPoiList[0];
};

export const isNonEmptyObject = (value: unknown): value is Record<string, unknown> => {
    return typeof value === 'object' && value !== null && !Array.isArray(value) && Object.keys(value).length > 0;
};
